import type { WallCard, WallConnection, WallShape } from '@walls/store';
import {
  getRenderedConnections,
  type ConnectionLinePoints,
} from '../connections/connectionGeometry';
import { getSelectableBoardElements } from '../positioning/cardRects';
import type { BoardRect } from '../selection/utils/selectionGeometry';

export interface ExportBounds extends BoardRect {
  contentHeight: number;
  contentWidth: number;
}

const emptyBoardSize = 800;
const exportPadding = 80;

export function getWallExportBounds(
  cards: WallCard[],
  connections: WallConnection[],
  shapes: WallShape[]
): ExportBounds {
  const elementRects = getSelectableBoardElements(cards, shapes).map((element) => element.rect);
  const connectionRects = getRenderedConnections(cards, connections).map(({ linePoints }) =>
    getConnectionBounds(linePoints)
  );
  const rects = [...elementRects, ...connectionRects];

  if (rects.length === 0) {
    return {
      contentHeight: emptyBoardSize,
      contentWidth: emptyBoardSize,
      height: emptyBoardSize + exportPadding * 2,
      width: emptyBoardSize + exportPadding * 2,
      x: -exportPadding,
      y: -exportPadding,
    };
  }

  const left = Math.min(...rects.map((rect) => rect.x));
  const top = Math.min(...rects.map((rect) => rect.y));
  const right = Math.max(...rects.map((rect) => rect.x + rect.width));
  const bottom = Math.max(...rects.map((rect) => rect.y + rect.height));
  const contentWidth = Math.max(1, Math.ceil(right - left));
  const contentHeight = Math.max(1, Math.ceil(bottom - top));

  return {
    contentHeight,
    contentWidth,
    height: contentHeight + exportPadding * 2,
    width: contentWidth + exportPadding * 2,
    x: Math.floor(left - exportPadding),
    y: Math.floor(top - exportPadding),
  };
}

export function getConnectionBounds({ endPoint, startPoint }: ConnectionLinePoints): BoardRect {
  const x = Math.min(startPoint.x, endPoint.x) - 32;
  const y = Math.min(startPoint.y, endPoint.y) - 32;

  return {
    height: Math.abs(endPoint.y - startPoint.y) + 64,
    width: Math.abs(endPoint.x - startPoint.x) + 64,
    x,
    y,
  };
}
